/**
 * grind·yssey size gate (Board 3).
 *
 * The colour is preselected, the size is not. Until the shopper picks a size:
 *   - Add to cart is disabled and a "Pick a size" hint shows under it
 *   - no size pill looks selected
 *   - the mobile sticky bar reads "Pick a size" and scrolls to the size pills
 * Shopify always has a selected variant underneath, so this is a presentation gate only.
 * A link that carries ?variant= counts as a size already picked, unless that variant is the
 * first size of its colour: card links and the default colourway use that one to choose the
 * colour only, so the shopper still has to pick a size.
 */

const UNPICKED_ATTRIBUTE = 'data-gy-size-unpicked';

/** @type {Set<string>} Product ids whose size has been picked in this page view. */
const pickedProducts = new Set();

/**
 * @param {Element} scope
 * @returns {HTMLElement | null}
 */
function findSizeFieldset(scope) {
  return scope.querySelector('[data-gy-size-fieldset]');
}

/**
 * @param {HTMLElement} section
 * @returns {string | undefined}
 */
function productIdOf(section) {
  return section.querySelector('variant-picker')?.dataset.productId;
}

/**
 * Applies the gate (or lifts it) on one product section.
 * @param {HTMLElement} section
 */
function applyGate(section) {
  const fieldset = findSizeFieldset(section);
  const productId = productIdOf(section);
  if (!fieldset || !productId) return;

  const isPicked = pickedProducts.has(productId);
  const addButton = section.querySelector('.product-details [name="add"]');
  const hint = section.querySelector('[data-gy-size-hint]');
  const checkedSize = fieldset.querySelector('input:checked');
  const checkedColour = section.querySelector('.gy-option--colour input:checked');

  section.toggleAttribute(UNPICKED_ATTRIBUTE, !isPicked);

  if (addButton instanceof HTMLButtonElement) {
    if (!isPicked && !addButton.disabled) {
      addButton.disabled = true;
      addButton.dataset.gyGated = 'true';
    } else if (isPicked && addButton.dataset.gyGated === 'true') {
      addButton.disabled = false;
      delete addButton.dataset.gyGated;
    }
  }

  if (hint instanceof HTMLElement) hint.hidden = isPicked;

  // Sticky bar: "Black" before a size is picked, "Black · 2XL" after
  const stickyBar = section.querySelector('.sticky-add-to-cart__bar');
  if (stickyBar instanceof HTMLElement) {
    const colour = checkedColour instanceof HTMLInputElement ? checkedColour.value : '';
    const size = isPicked && checkedSize instanceof HTMLInputElement ? checkedSize.value : '';
    const meta = [colour, size].filter(Boolean).join(' · ');
    stickyBar.dataset.gyMeta = meta;
    const stickyPrice = stickyBar.querySelector('.sticky-add-to-cart__price');
    if (stickyPrice instanceof HTMLElement) stickyPrice.dataset.gyMeta = meta;
  }

  // Size guide: highlight the picked size
  for (const row of document.querySelectorAll('[data-gy-size-row]')) {
    const isCurrent =
      isPicked && checkedSize instanceof HTMLInputElement && row.getAttribute('data-gy-size-row') === checkedSize.value;
    row.setAttribute('aria-selected', String(isCurrent));
  }
}

function applyAll() {
  for (const fieldset of document.querySelectorAll('[data-gy-size-fieldset]')) {
    const section = fieldset.closest('.shopify-section');
    if (section instanceof HTMLElement) applyGate(section);
  }
}

// A deep link to a variant counts as a picked size, except the first size (a colour-only link)
if (new URL(location.href).searchParams.has('variant')) {
  for (const picker of document.querySelectorAll('variant-picker')) {
    const sizes = picker.querySelectorAll('[data-gy-size-fieldset] input');
    if (sizes[0] instanceof HTMLInputElement && sizes[0].checked) continue;
    if (picker instanceof HTMLElement && picker.dataset.productId) pickedProducts.add(picker.dataset.productId);
  }
}

// Picking a size (including the pill that is already checked underneath) lifts the gate
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const label = target.closest('[data-gy-size-fieldset] label');
    if (label) {
      const section = label.closest('.shopify-section');
      const productId = section instanceof HTMLElement ? productIdOf(section) : undefined;
      if (section instanceof HTMLElement && productId) {
        pickedProducts.add(productId);
        applyGate(section);
      }
      return;
    }

    // Sticky bar button before a size is picked: scroll to the size pills instead of adding
    const stickyButton = target.closest('.sticky-add-to-cart__button');
    if (stickyButton) {
      const section = stickyButton.closest('.shopify-section');
      if (section instanceof HTMLElement && section.hasAttribute(UNPICKED_ATTRIBUTE)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const fieldset = findSizeFieldset(section);
        fieldset?.scrollIntoView({ block: 'center', behavior: 'auto' });
        const firstInput = fieldset?.querySelector('input');
        if (firstInput instanceof HTMLElement) firstInput.focus({ preventScroll: true });
      }
    }
  },
  true
);

// Horizon re-renders the picker and buttons when the variant changes: re-apply after each update
const observer = new MutationObserver(() => {
  observer.disconnect();
  applyAll();
  observe();
});

function observe() {
  for (const fieldset of document.querySelectorAll('[data-gy-size-fieldset]')) {
    const section = fieldset.closest('.shopify-section');
    if (section) {
      observer.observe(section, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'checked'] });
    }
  }
}

applyAll();
observe();
